/**
 * The decorations of the designed banner templates.  Owner, 2026-10-22: "premade templates, beautiful, like the mosque
 * banner templates on the web, so admin can select and write on those".
 *
 * A decoration is drawn behind the text and never carries text itself, so the contrast rule stays the palette's
 * (checkPalette): every ornament is in the palette's accent or foreground at reduced strength, on the palette's ground.
 *
 * One banner hangs in two shapes: a whole 16:9 screen, and the right half of a split screen, which is nearly square
 * (8:9). One flat picture would be cropped or squashed in one of them, so a decoration is a few PIECES, each pinned to
 * an edge or a corner in percent of the banner (`box`), each its own small SVG. The text sits in `inset`, the part
 * the ornaments leave free. The TV, the panel's preview and the app place the same pieces the same way.
 *
 * A round ornament (a crescent, a lantern, a rosette) gets a box in BOTH directions and is fitted inside it, pinned to
 * its corner (`meet`), so it takes its size from the smaller side: right on a wide screen, and not twice too big on a
 * TV hung on its end (checked 2026-10-22 in all four shapes: wide and tall, whole and split).
 *
 * The SVG is made here from fixed shapes and the palette's own colours — nothing a person typed goes into it.
 */

/** An eight-pointed star (khatam): a square and the same square turned 45°. */
function star8(cx, cy, r) {
  const s = r * Math.SQRT1_2;
  const f = (n) => Math.round(n * 100) / 100;
  return `M${f(cx - s)} ${f(cy - s)}H${f(cx + s)}V${f(cy + s)}H${f(cx - s)}Z`
    + `M${f(cx)} ${f(cy - r)}L${f(cx + r)} ${f(cy)}L${f(cx)} ${f(cy + r)}L${f(cx - r)} ${f(cy)}Z`;
}

/** A five-pointed star. */
function star5(cx, cy, r) {
  const pts = [];
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5, rr = i % 2 ? r * 0.42 : r;
    pts.push(`${Math.round((cx + rr * Math.cos(a)) * 10) / 10} ${Math.round((cy + rr * Math.sin(a)) * 10) / 10}`);
  }
  return `M${pts.join('L')}Z`;
}

/** A four-pointed sparkle. */
function sparkle(cx, cy, r) {
  const k = r * 0.22;
  return `M${cx} ${cy - r}Q${cx + k} ${cy - k} ${cx + r} ${cy}Q${cx + k} ${cy + k} ${cx} ${cy + r}`
    + `Q${cx - k} ${cy + k} ${cx - r} ${cy}Q${cx - k} ${cy - k} ${cx} ${cy - r}Z`;
}

const svg = (viewBox, body, fit = 'xMidYMid meet') =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" preserveAspectRatio="${fit}" width="100%" height="100%" aria-hidden="true">${body}</svg>`;

/** The eight-point lattice, as one tile of a pattern: a star, and lines from its points to the tile's edges. */
function lattice(id, colour, opacity) {
  return `<defs><pattern id="${id}" width="100" height="100" patternUnits="userSpaceOnUse">`
    + `<path d="${star8(50, 50, 26)}" fill="none" stroke="${colour}" stroke-width="2"/>`
    + `<path d="M50 0V24M50 76V100M0 50H24M76 50H100" stroke="${colour}" stroke-width="2"/>`
    + `<circle cx="0" cy="0" r="7" fill="none" stroke="${colour}" stroke-width="2"/>`
    + `<circle cx="100" cy="0" r="7" fill="none" stroke="${colour}" stroke-width="2"/>`
    + `<circle cx="0" cy="100" r="7" fill="none" stroke="${colour}" stroke-width="2"/>`
    + `<circle cx="100" cy="100" r="7" fill="none" stroke="${colour}" stroke-width="2"/>`
    + `</pattern></defs><rect width="800" height="800" fill="url(#${id})" opacity="${opacity}"/>`;
}

const FULL = { top: 0, right: 0, bottom: 0, left: 0 };
/** A pattern's id names its colour: two banners on one page (the gallery, the preview) in different palettes would
 *  otherwise share the first one's pattern — an SVG id is one per document. */
const hex = (c) => String(c).replace(/[^0-9a-fA-F]/g, '').toLowerCase();

/** A sparse field of sparkles and dots, kept to the edges so none sits behind a line of text. */
function starField(p, opacity) {
  const spots = [[90, 70, 14], [260, 40, 8], [420, 110, 6], [1180, 60, 9], [1340, 120, 6], [1520, 50, 12],
    [60, 420, 7], [1550, 380, 8], [120, 820, 10], [320, 860, 6], [1260, 840, 7], [1480, 800, 12]];
  return svg('0 0 1600 900', spots.map(([x, y, r], i) => (i % 3 === 2
    ? `<circle cx="${x}" cy="${y}" r="${r / 2}" fill="${p.accent}" opacity="${opacity}"/>`
    : `<path d="${sparkle(x, y, r)}" fill="${p.accent}" opacity="${opacity}"/>`)).join(''), 'xMidYMid slice');
}

/** One hanging lantern, its hook at (x, y) on a string of `drop`. */
function lantern(p, x, y, drop) {
  const t = y + drop;
  return `<path d="M${x} ${y}V${t}" stroke="${p.accent}" stroke-width="2" opacity=".8"/>`
    + `<circle cx="${x}" cy="${t + 4}" r="5" fill="none" stroke="${p.accent}" stroke-width="2.5"/>`
    + `<path d="M${x - 14} ${t + 10}H${x + 14}L${x + 26} ${t + 30}H${x - 26}Z" fill="${p.accent}"/>`
    + `<path d="M${x - 26} ${t + 30}H${x + 26}L${x + 32} ${t + 64}L${x + 26} ${t + 98}H${x - 26}L${x - 32} ${t + 64}Z"`
    + ` fill="${p.accent}" fill-opacity=".16" stroke="${p.accent}" stroke-width="3"/>`
    + `<path d="M${x - 15} ${t + 40}H${x + 15}L${x + 19} ${t + 64}L${x + 15} ${t + 88}H${x - 15}L${x - 19} ${t + 64}Z" fill="${p.accent}" opacity=".5"/>`
    + `<path d="M${x} ${t + 34}V${t + 94}M${x - 26} ${t + 64}H${x + 26}" stroke="${p.accent}" stroke-width="2" opacity=".9"/>`
    + `<path d="M${x - 20} ${t + 98}H${x + 20}L${x + 10} ${t + 112}H${x - 10}Z" fill="${p.accent}"/>`
    + `<path d="M${x} ${t + 112}V${t + 126}" stroke="${p.accent}" stroke-width="2"/><circle cx="${x}" cy="${t + 130}" r="4" fill="${p.accent}"/>`;
}

/** A mosque on the skyline: a main dome with its crescent, two side domes, two minarets, a ground line. */
function skyline(p) { return svg('0 0 1600 300', skylineShapes(p.accent, p.bg), 'xMidYMax slice'); }

/** The skyline's shapes in one colour `a`; the door is cut in `door` (none: left open). */
function skylineShapes(a, door) {
  const minaret = (x) => `<rect x="${x - 13}" y="70" width="26" height="230" fill="${a}"/>`
    + `<rect x="${x - 20}" y="118" width="40" height="10" fill="${a}"/><rect x="${x - 20}" y="190" width="40" height="10" fill="${a}"/>`
    + `<path d="M${x - 13} 70L${x} 22L${x + 13} 70Z" fill="${a}"/><path d="M${x} 22V6" stroke="${a}" stroke-width="3"/>`;
  const dome = (cx, w, base, h) => `<path d="M${cx - w} ${base}C${cx - w} ${base - h * 0.55} ${cx - w * 0.35} ${base - h * 0.8} ${cx} ${base - h}`
    + `C${cx + w * 0.35} ${base - h * 0.8} ${cx + w} ${base - h * 0.55} ${cx + w} ${base}Z" fill="${a}"/>`;
  return (
    `<g opacity=".8">`
    + `<rect x="0" y="282" width="1600" height="18" fill="${a}"/>`
    + `<rect x="140" y="236" width="230" height="64" fill="${a}"/>${dome(255, 70, 236, 70)}`
    + `<rect x="1230" y="236" width="230" height="64" fill="${a}"/>${dome(1345, 70, 236, 70)}`
    + `</g><g>`
    + `<rect x="600" y="176" width="400" height="124" fill="${a}"/>${dome(800, 170, 180, 140)}`
    + `<path d="M800 40V24" stroke="${a}" stroke-width="4"/>`
    + `<path d="M800 2a10 10 0 1 0 0.01 0ZM802.5 4.5a7.5 7.5 0 1 1 -0.01 0Z" fill="${a}" fill-rule="evenodd"/>`
    + `<rect x="440" y="210" width="150" height="90" fill="${a}"/>${dome(515, 62, 212, 70)}`
    + `<rect x="1010" y="210" width="150" height="90" fill="${a}"/>${dome(1085, 62, 212, 70)}`
    + `${minaret(420)}${minaret(1180)}`
    + (door ? `<path d="M760 300V240C760 214 840 214 840 240V300Z" fill="${door}"/>` : '')
    + `</g>`);
}

/** Layered mosque silhouettes, the far row fainter (CJAS's posts, 2026-10-22). `c`: the colour. */
function cityscape(c, near, far) {
  const dome = (cx, w, base, h) => `M${cx - w} ${base}C${cx - w} ${base - h * 0.55} ${cx - w * 0.35} ${base - h * 0.8} ${cx} ${base - h}`
    + `C${cx + w * 0.35} ${base - h * 0.8} ${cx + w} ${base - h * 0.55} ${cx + w} ${base}Z`;
  const tower = (x, top) => `M${x - 9} 400V${top + 30}L${x} ${top}L${x + 9} ${top + 30}Z`;
  const row = (items) => items.map(([k, ...v]) => (k === 'd' ? dome(...v) : k === 't' ? tower(...v) : `M${v[0]} 400V${v[1]}H${v[2]}V400Z`)).join('');
  return `<path d="${row([['r', 40, 300, 220], ['d', 130, 70, 300, 90], ['t', 250, 150], ['r', 280, 290, 470], ['d', 375, 95, 290, 130], ['t', 500, 120], ['r', 520, 320, 640], ['d', 580, 60, 320, 80]])}" fill="${c}" opacity="${far}"/>`
    + `<path d="${row([['r', 0, 340, 120], ['d', 60, 60, 340, 70], ['t', 150, 200], ['r', 190, 300, 430], ['d', 310, 120, 300, 170], ['t', 455, 190], ['r', 480, 350, 600], ['d', 540, 60, 350, 60]])}" fill="${c}" opacity="${near}"/>`;
}

/** A mosque in outline only: the line drawing of the Eid post. */
function lineMosque(c) {
  const dome = (cx, w, base, h) => `M${cx - w} ${base}C${cx - w} ${base - h * 0.55} ${cx - w * 0.35} ${base - h * 0.8} ${cx} ${base - h}`
    + `C${cx + w * 0.35} ${base - h * 0.8} ${cx + w} ${base - h * 0.55} ${cx + w} ${base}`;
  const minaret = (x, top) => `M${x - 12} 300V${top + 50}H${x + 12}V300M${x - 17} ${top + 50}H${x + 17}M${x - 12} ${top + 50}L${x} ${top}L${x + 12} ${top + 50}M${x - 17} ${top + 120}H${x + 17}`;
  return `<path d="${dome(400, 120, 170, 140)}M280 170H520V300M280 170V300${dome(250, 55, 220, 75)}M195 220H305V300M195 220V300${dome(550, 55, 220, 75)}M495 220H605V300M495 220V300`
    + `${minaret(140, 60)}${minaret(660, 60)}M400 30V12M80 300H720M375 300V250C375 232 425 232 425 250V300" fill="none" stroke="${c}" stroke-width="2.5" stroke-linejoin="round" vector-effect="non-scaling-stroke"/>`;
}

/** Three small birds in flight. */
const birds = (c) => [[40, 40, 1], [90, 20, 0.8], [120, 55, 0.65], [170, 30, 0.55]].map(([x, y, k]) =>
  `<path d="M${x - 14 * k} ${y - 6 * k}Q${x - 6 * k} ${y - 7 * k} ${x} ${y}Q${x + 6 * k} ${y - 7 * k} ${x + 14 * k} ${y - 6 * k}" fill="none" stroke="${c}" stroke-width="${2.6 * k}" stroke-linecap="round"/>`).join('');

/** A three-by-three grid of gold dots. */
const dots = (c) => [0, 1, 2].flatMap((i) => [0, 1, 2].map((j) => `<circle cx="${10 + i * 20}" cy="${10 + j * 20}" r="3.2" fill="${c}"/>`)).join('');

/** A cluster of lanterns hanging at one side, on strings of different lengths (the light Jumu'ah post). */
function lanternCluster(p) {
  return lantern(p, 60, 0, 40) + lantern(p, 150, 0, 120) + lantern(p, 230, 0, 20) + lantern(p, 300, 0, 80);
}

/** The pieces of each decoration. `p`: a palette { bg, fg, accent }. */
const ART = {
  /** A mihrab arch around the text, the lattice faint outside it. */
  arch: (p) => ({
    pieces: [
      { box: FULL, svg: svg('0 0 800 800', lattice(`lt-arch-${hex(p.accent)}`, p.accent, 0.1), 'xMidYMid slice') },
      { box: { top: 7, right: 11, bottom: 0, left: 11 }, svg: svg('0 0 100 100',
        `<path d="M0 100V40C0 20 30 9 50 0C70 9 100 20 100 40V100Z" fill="${p.bg}" stroke="${p.accent}" stroke-width="3" vector-effect="non-scaling-stroke"/>`
        + `<path d="M3.5 100V41C3.5 23 32 12.5 50 4.5C68 12.5 96.5 23 96.5 41V100" fill="none" stroke="${p.accent}" stroke-width="1.2" opacity=".7" vector-effect="non-scaling-stroke"/>`, 'none') },
      { box: { top: 0, right: 0, left: 0, height: 13 }, svg: svg('0 0 100 100',
        `<circle cx="50" cy="56" r="30" fill="${p.bg}"/><path d="${star8(50, 56, 26)}" fill="${p.accent}"/>`
        + `<path d="${star8(50, 56, 12)}" fill="${p.bg}"/><circle cx="50" cy="56" r="5" fill="${p.accent}"/>`) },
    ],
    inset: { top: 30, right: 17, bottom: 9, left: 17 },
  }),
  /** A crescent and a star in the upper corner, sparkles at the edges, a small ornament under the text. */
  crescent: (p) => ({
    pieces: [
      { box: FULL, svg: starField(p, 0.55) },
      { box: { top: 5, right: 4, width: 30, height: 34 }, svg: svg('0 0 100 100',
        `<path d="M50 10a40 40 0 1 0 0.01 0ZM58 14a30 30 0 1 1 -0.01 0Z" fill="${p.accent}" fill-rule="evenodd"/>`
        + `<path d="${star5(78, 30, 9)}" fill="${p.accent}"/>`, 'xMaxYMin meet') },
      { box: { bottom: 5, left: 25, right: 25, height: 7 }, svg: svg('0 0 600 30',
        `<path d="M40 15H262M338 15H560" stroke="${p.accent}" stroke-width="2" opacity=".8"/>`
        + `<path d="${star8(300, 15, 13)}" fill="${p.accent}"/><circle cx="276" cy="15" r="3" fill="${p.accent}"/><circle cx="324" cy="15" r="3" fill="${p.accent}"/>`) },
    ],
    inset: { top: 16, right: 12, bottom: 15, left: 12 },
  }),
  /** Lanterns hanging from the top on both sides. */
  lanterns: (p) => {
    const group = lantern(p, 60, 0, 70) + lantern(p, 180, 0, 150) + lantern(p, 300, 0, 30);
    return {
      pieces: [
        { box: FULL, svg: starField(p, 0.4) },
        { box: { top: 0, left: 2, width: 46, height: 44 }, svg: svg('0 0 360 400', group, 'xMinYMin meet') },
        { box: { top: 0, right: 2, width: 46, height: 44 }, svg: svg('0 0 360 400', `<g transform="translate(360 0) scale(-1 1)">${group}</g>`, 'xMaxYMin meet') },
      ],
      inset: { top: 30, right: 10, bottom: 10, left: 10 },
    };
  },
  /** A mosque along the bottom, the night's sparkles above. */
  skyline: (p) => ({
    pieces: [
      { box: FULL, svg: starField(p, 0.45) },
      { box: { bottom: 0, left: 0, right: 0, height: 33 }, svg: skyline(p) },
    ],
    inset: { top: 9, right: 10, bottom: 35, left: 10 },
  }),
  /** The lattice over the whole ground, a plain plate for the text in the middle. */
  pattern: (p) => ({
    pieces: [
      { box: FULL, svg: svg('0 0 800 800', lattice(`lt-pattern-${hex(p.accent)}`, p.accent, 0.22), 'xMidYMid slice') },
      { box: { top: 9, right: 7, bottom: 9, left: 7 }, svg: svg('0 0 100 100',
        `<rect x="0" y="0" width="100" height="100" fill="${p.bg}" stroke="${p.accent}" stroke-width="3" vector-effect="non-scaling-stroke"/>`
        + `<rect x="1.6" y="2.8" width="96.8" height="94.4" fill="none" stroke="${p.accent}" stroke-width="1" opacity=".6" vector-effect="non-scaling-stroke"/>`, 'none') },
    ],
    inset: { top: 14, right: 11, bottom: 14, left: 11 },
  }),
  /** A double frame with a rosette in each corner. */
  frame: (p) => {
    const rosette = (fit) => svg('0 0 100 100', `<circle cx="50" cy="50" r="34" fill="${p.bg}"/>`
      + `<path d="${star8(50, 50, 32)}" fill="${p.accent}"/><path d="${star8(50, 50, 17)}" fill="${p.bg}"/>`
      + `<circle cx="50" cy="50" r="6" fill="${p.accent}"/>`, fit);
    return {
      pieces: [
        { box: { top: 5, right: 3.5, bottom: 5, left: 3.5 }, svg: svg('0 0 100 100',
          `<rect x="0" y="0" width="100" height="100" fill="none" stroke="${p.accent}" stroke-width="2.5" vector-effect="non-scaling-stroke"/>`
          + `<rect x="1.4" y="2.5" width="97.2" height="95" fill="none" stroke="${p.accent}" stroke-width="1" opacity=".6" vector-effect="non-scaling-stroke"/>`, 'none') },
        { box: { top: 0, left: 0, width: 12, height: 15 }, svg: rosette('xMinYMin meet') },
        { box: { top: 0, right: 0, width: 12, height: 15 }, svg: rosette('xMaxYMin meet') },
        { box: { bottom: 0, left: 0, width: 12, height: 15 }, svg: rosette('xMinYMax meet') },
        { box: { bottom: 0, right: 0, width: 12, height: 15 }, svg: rosette('xMaxYMax meet') },
        { box: { top: 1.5, left: 0, right: 0, height: 7 }, svg: svg('0 0 300 30',
          `<path d="M60 15H128M172 15H240" stroke="${p.accent}" stroke-width="2"/><path d="${sparkle(150, 15, 14)}" fill="${p.accent}"/>`) },
      ],
      inset: { top: 14, right: 12, bottom: 14, left: 12 },
    };
  },
};

/** The decoration `id` in palette `p`, or null for a plain template. */
function art(id, p) {
  const make = Object.hasOwn(ART, id) ? ART[id] : null;
  return make && p ? { id, ...make(p) } : null;
}

/** A piece's place as CSS, for the TV and the panel (the app turns the same numbers into its own styles). */
function pieceStyle(box) {
  const out = ['position:absolute'];
  for (const k of ['top', 'right', 'bottom', 'left']) if (typeof box[k] === 'number') out.push(`${k}:${box[k]}%`);
  if (typeof box.width === 'number') out.push(`width:${box.width}%`);
  if (typeof box.height === 'number') out.push(`height:${box.height}%`);
  return out.join(';');
}

/** The decoration as HTML: one absolutely placed box per piece. For the TV and the panel's preview. */
function artHtml(a) {
  if (!a) return '';
  return a.pieces.map((pc) => `<div style="${pieceStyle(pc.box)}">${pc.svg}</div>`).join('');
}

/* ---- in the style of CJAS's own posts (owner's Drive folder, 2026-10-22) ---- */
Object.assign(ART, {
  /** Light: the faint lattice, a green city on the bottom left, gold lanterns and stars on the top right. */
  daylight: (p) => ({
    pieces: [
      { box: FULL, svg: svg('0 0 800 800', lattice(`lt-day-${hex(p.fg)}`, p.fg, 0.07), 'xMidYMid slice') },
      { box: FULL, svg: starField(p, 0.6) },
      { box: { bottom: 0, left: 0, width: 62, height: 36 }, svg: svg('0 0 640 400', cityscape(p.fg, 0.55, 0.22), 'xMinYMax meet') },
      { box: { top: 0, right: 3, width: 32, height: 40 }, svg: svg('0 0 360 400', lanternCluster(p), 'xMaxYMin meet') },
    ],
    inset: { top: 12, right: 14, bottom: 26, left: 14 },
  }),
  /** Light: a large green crescent, a mosque drawn in one line behind the text, a green curve along the bottom. */
  lineart: (p) => ({
    pieces: [
      { box: FULL, svg: svg('0 0 800 800', lattice(`lt-line-${hex(p.fg)}`, p.fg, 0.05), 'xMidYMid slice') },
      { box: { top: 3, left: 0, right: 0, height: 22 }, svg: svg('0 0 100 100',
        `<path d="M50 6a44 44 0 1 0 0.01 0ZM58 10a34 34 0 1 1 -0.01 0Z" fill="${p.fg}" fill-rule="evenodd"/>`, 'xMidYMin meet') },
      // A watermark behind the text, faint enough that every line over it still reads.
      { box: { bottom: 8, left: 8, right: 8, height: 50 }, svg: svg('0 0 800 300', `<g opacity=".18">${lineMosque(p.fg)}</g>`, 'xMidYMax meet') },
      { box: { bottom: 0, left: 0, right: 0, height: 9 }, svg: svg('0 0 100 10',
        `<path d="M0 10V6C30 0 70 0 100 6V10Z" fill="${p.fg}"/><path d="M0 4.5C30 -1.5 70 -1.5 100 4.5" fill="none" stroke="${p.accent}" stroke-width="1.5" vector-effect="non-scaling-stroke"/>`, 'none') },
    ],
    inset: { top: 24, right: 12, bottom: 16, left: 12 },
  }),
  /** Deep green with a light from above, a dark city along the bottom, birds and gold dots (the Rabi-ul-Awwal post). */
  spotlight: (p) => ({
    pieces: [
      { box: FULL, svg: svg('0 0 1600 900', `<defs><radialGradient id="sp-${hex(p.bg)}" cx="50%" cy="0%" r="75%">`
        + `<stop offset="0" stop-color="#FFFFFF" stop-opacity=".28"/><stop offset=".55" stop-color="#FFFFFF" stop-opacity=".06"/>`
        + `<stop offset="1" stop-color="#FFFFFF" stop-opacity="0"/></radialGradient></defs><rect width="1600" height="900" fill="url(#sp-${hex(p.bg)})"/>`, 'none') },
      { box: { bottom: 0, left: 0, right: 0, height: 34 }, svg: svg('0 0 1600 300', `<g opacity=".3">${skylineShapes('#000000')}</g>`, 'xMidYMax slice') },
      { box: { top: 8, right: 4, width: 16, height: 14 }, svg: svg('0 0 200 80', birds(p.fg), 'xMaxYMin meet') },
      { box: { top: 30, left: 4, width: 7, height: 12 }, svg: svg('0 0 60 60', dots(p.accent), 'xMinYMid meet') },
      { box: { top: 30, right: 4, width: 7, height: 12 }, svg: svg('0 0 60 60', dots(p.accent), 'xMaxYMid meet') },
    ],
    inset: { top: 9, right: 13, bottom: 30, left: 13 },
  }),
  /** Light notice: green curves in two corners over the faint lattice, a few gold stars. */
  swoosh: (p) => ({
    pieces: [
      { box: FULL, svg: svg('0 0 800 800', lattice(`lt-sw-${hex(p.fg)}`, p.fg, 0.07), 'xMidYMid slice') },
      { box: { bottom: 0, left: 0, right: 0, height: 16 }, svg: svg('0 0 100 20',
        `<path d="M0 20V9C25 2 55 16 100 4V20Z" fill="${p.fg}"/><path d="M0 6C25 -1 55 13 100 1" fill="none" stroke="${p.accent}" stroke-width="1.5" vector-effect="non-scaling-stroke"/>`, 'none') },
      { box: { top: 0, left: 0, width: 26, height: 30 }, svg: svg('0 0 100 100',
        `<path d="M0 0H100C70 10 40 40 0 100Z" fill="${p.fg}" opacity=".9"/><path d="M0 100C40 46 74 14 100 4" fill="none" stroke="${p.accent}" stroke-width="1.5" vector-effect="non-scaling-stroke"/>`, 'xMinYMin meet') },
      { box: { top: 6, right: 5, width: 12, height: 18 }, svg: svg('0 0 100 100',
        `<path d="${star5(70, 30, 22)}" fill="${p.accent}"/><path d="${star5(30, 62, 12)}" fill="${p.accent}" opacity=".7"/><path d="${sparkle(80, 80, 10)}" fill="${p.accent}" opacity=".6"/>`, 'xMaxYMin meet') },
    ],
    inset: { top: 15, right: 13, bottom: 20, left: 13 },
  }),
});

/* ---- «Bilde med tekst» and the studio's ornament choice (banner studio, 2026-10-31) ---- */
Object.assign(ART, {
  /**
   * The scrim over a photo: the palette's ground, translucent, denser towards the bottom, so the palette's text reads
   * over any picture. The photo template's own, always — never something staff pick or drop (ORNAMENTS leaves it out).
   */
  scrim: (p) => ({
    pieces: [
      { box: FULL, svg: svg('0 0 1600 900', `<defs><linearGradient id="sc-${hex(p.bg)}" x1="0" y1="0" x2="0" y2="1">`
        + `<stop offset="0" stop-color="${p.bg}" stop-opacity=".58"/><stop offset=".5" stop-color="${p.bg}" stop-opacity=".72"/>`
        + `<stop offset="1" stop-color="${p.bg}" stop-opacity=".9"/></linearGradient></defs><rect width="1600" height="900" fill="url(#sc-${hex(p.bg)})"/>`, 'none') },
      { box: { bottom: 0, left: 0, right: 0, height: 2.2 }, svg: svg('0 0 100 10', `<rect width="100" height="10" fill="${p.accent}"/>`, 'none') },
    ],
    inset: { top: 12, right: 10, bottom: 14, left: 10 },
  }),
});

/**
 * The ornaments staff may put on ANY text design (the studio's «Pynt»), in the order shown, with their names. Still no
 * canvas: an ornament is a fixed set of pieces in the palette's colours, and the text keeps to the space it leaves.
 */
const ORNAMENTS = [
  ['arch', 'Bue'], ['crescent', 'Halvmåne'], ['lanterns', 'Lykter'], ['skyline', 'Moské'], ['pattern', 'Mønster'],
  ['frame', 'Ramme'], ['daylight', 'Dagslys'], ['lineart', 'Strektegning'], ['spotlight', 'Lysglimt'], ['swoosh', 'Sving'],
].map(([id, name_no]) => ({ id, name_no }));

module.exports = { ART, art, artHtml, pieceStyle, star8, star5, sparkle, MOTIFS: Object.keys(ART), ORNAMENTS };
